import { useEffect } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Shared dialog behavior for `Modal` and the in-game `PauseOverlay`:
 * - moves focus INTO the container on open (first focusable element, or
 *   the container itself if it has none),
 * - Tab/Shift+Tab cycle WITHIN the container only (never escape to the
 *   page behind the scrim),
 * - Escape calls `onClose` (if provided),
 * - restores focus to whatever was focused right before open, on close --
 *   so closing a modal opened from a button returns focus there instead of
 *   silently dropping to `<body>` (a real a11y regression a screen-reader
 *   user would hit every time).
 *
 * `active` gates all of this so a container that is conditionally rendered
 * (or briefly present-but-inactive) never steals focus/keys it shouldn't. */
export function useFocusTrap(
  containerRef: React.RefObject<HTMLElement>,
  active: boolean,
  onClose?: () => void,
): void {
  useEffect(() => {
    if (!active) return;
    const maybeContainer = containerRef.current;
    if (!maybeContainer) return;
    // A fresh, non-nullable binding: TS narrows `containerRef.current` at
    // THIS statement, but does not retain that narrowing for uses inside
    // the nested closures below (`focusables`/`onKeyDown`) -- assigning
    // once into a variable with no `| null` in its inferred type sidesteps
    // that entirely, everywhere this closure captures it.
    const container: HTMLElement = maybeContainer;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    function focusables(): HTMLElement[] {
      return Array.from(container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
        (el) => el.offsetParent !== null || el === document.activeElement,
      );
    }

    const initial = focusables();
    (initial[0] ?? container).focus({ preventScroll: true });

    function onKeyDown(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        // Only intercept Escape when this container actually OWNS closing
        // itself (`onClose` provided) -- PauseOverlay passes no `onClose`
        // here because Esc-to-resume is already the existing, spec'd
        // mechanism (GameScreen's `attachKeyboard`, gated by `canResume`);
        // trapping/stopping Escape here too would silently swallow that
        // event before it ever reaches the window-level listener. Modals
        // stacked ON TOP of the pause menu (Help/Settings) DO pass
        // `onClose`, so their own Escape close is self-contained and never
        // leaks through to also resume the game underneath.
        if (onClose) {
          e.stopPropagation();
          onClose();
        }
        return;
      }
      if (e.key !== 'Tab') return;
      const items = focusables();
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const current = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (current === first || !container.contains(current)) {
          e.preventDefault();
          last.focus();
        }
      } else if (current === last || !container.contains(current)) {
        e.preventDefault();
        first.focus();
      }
    }

    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      // Only restore focus if it's still somewhere reasonable to steal it
      // back from (a genuinely still-mounted, visible element) -- guards
      // against fighting some OTHER effect that already moved focus on
      // unmount for its own reason.
      if (previouslyFocused && document.contains(previouslyFocused)) {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [active, containerRef, onClose]);
}
